import _ from 'lodash';

import {
  DisplayTableV2FetchRequest,
  DisplayTableV2FetchResponse,
  DisplayTableV2FieldFilter,
  DisplayTableV2FilterOperator,
  DisplayTableV2LegacyColumn,
  DisplayTableV2LegacyResponse,
  DisplayTableV2MuiFilterItem,
  DisplayTableV2MuiSortItem,
  DisplayTableV2RequestBuilderInput,
  DisplayTableV2SortDirection,
} from 'types/api/Tenant/AROKMS/DisplayTableV2Types';
import {
  DataInputPanelOptions,
  DynamicDataItem,
} from 'types/api/Tenant/AROKMS/DisplayTableTypes';
import { KTreeNode } from 'types/api/Tenant/AROKMS/KTreeDataTypes';
import { flattenTreeData } from 'utils/Object';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 50;

const BACKEND_FILTER_OPERATORS = new Set<DisplayTableV2FilterOperator>([
  'CONTAINS',
  'STARTS_WITH',
  'ENDS_WITH',
  'EQUALS',
  'NOT_EQUALS',
  'IN',
  'GT',
  'GTE',
  'LT',
  'LTE',
  'BETWEEN',
  'IS_NULL',
  'IS_NOT_NULL',
]);

const MUI_FILTER_OPERATOR_MAP: Record<string, DisplayTableV2FilterOperator> = {
  contains: 'CONTAINS',
  dropdownfilter: 'CONTAINS',
  startswith: 'STARTS_WITH',
  endswith: 'ENDS_WITH',
  equals: 'EQUALS',
  '=': 'EQUALS',
  is: 'EQUALS',
  notequals: 'NOT_EQUALS',
  '!=': 'NOT_EQUALS',
  not: 'NOT_EQUALS',
  isanyof: 'IN',
  '>': 'GT',
  '>=': 'GTE',
  '<': 'LT',
  '<=': 'LTE',
  after: 'GT',
  onorafter: 'GTE',
  before: 'LT',
  onorbefore: 'LTE',
  between: 'BETWEEN',
  isempty: 'IS_NULL',
  isnotempty: 'IS_NOT_NULL',
};

const VALUELESS_FILTER_OPERATORS = new Set<DisplayTableV2FilterOperator>(['IS_NULL', 'IS_NOT_NULL']);

export function getDisplayTableV2PrefetchPages(currentPage: number, totalPages: number): number[] {
  return [currentPage + 1, currentPage + 2].filter((page) => page <= totalPages);
}

export function getInitialDisplayTableV2HierarchyFilters(
  panelOptions: DataInputPanelOptions[],
  getSearchParam: (tableDefinitionId: string) => string | null
): Record<string, string> {
  return panelOptions.reduce<Record<string, string>>((filters, panelOption) => {
    const options = flattenTreeData(panelOption.options) as KTreeNode[];
    const selectedValue = getSearchParam(panelOption.tableDefinitionId);
    const selectedOption =
      options.find((option) => option.value === selectedValue) ||
      options.find((option) => option.level === 0);

    if (selectedOption) filters[panelOption.tableDefinitionId] = selectedOption.value;

    return filters;
  }, {});
}

export function areDisplayTableV2FilterItemsEqual(
  currentFilters: DisplayTableV2MuiFilterItem[],
  nextFilters: DisplayTableV2MuiFilterItem[]
): boolean {
  const normalizeFilters = (filters: DisplayTableV2MuiFilterItem[]) =>
    filters.map(({ columnField, operatorValue, operator, value }) => ({
      columnField,
      operatorValue,
      operator,
      value,
    }));

  return _.isEqual(normalizeFilters(currentFilters), normalizeFilters(nextFilters));
}

export function areDisplayTableV2SortModelsEqual(
  currentSort: DisplayTableV2MuiSortItem[],
  nextSort: DisplayTableV2MuiSortItem[]
): boolean {
  return _.isEqual(currentSort, nextSort);
}

export function areDisplayTableV2HierarchyFiltersEqual(
  currentFilters: Record<string, string>,
  nextFilters: Record<string, string>
): boolean {
  return _.isEqual(currentFilters, nextFilters);
}

export function shouldApplyDisplayTableV2HierarchyFilters(
  currentFilters: Record<string, string>,
  nextFilters: Record<string, string>
): boolean {
  const isTransientEmptyEmission =
    Object.keys(currentFilters).length > 0 && Object.keys(nextFilters).length === 0;

  if (isTransientEmptyEmission) return false;

  return !areDisplayTableV2HierarchyFiltersEqual(currentFilters, nextFilters);
}

export function mapMuiFilterOperator(operator?: string): DisplayTableV2FilterOperator | undefined {
  const trimmedOperator = operator?.trim();
  if (!trimmedOperator) return undefined;

  if (BACKEND_FILTER_OPERATORS.has(trimmedOperator as DisplayTableV2FilterOperator)) {
    return trimmedOperator as DisplayTableV2FilterOperator;
  }

  return MUI_FILTER_OPERATOR_MAP[trimmedOperator.toLowerCase()];
}

function mapSortDirection(direction?: string): DisplayTableV2SortDirection | undefined {
  const normalizedDirection = direction?.trim().toUpperCase();
  if (normalizedDirection === 'ASC' || normalizedDirection === 'DESC') return normalizedDirection;
  return undefined;
}

function isBlankFilterValue(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
}

function toFilterValues(
  value: unknown,
  operator: DisplayTableV2FilterOperator,
  dataType?: string
): unknown[] | undefined {
  if (VALUELESS_FILTER_OPERATORS.has(operator)) return [];
  const values = Array.isArray(value) ? value : [value];
  if (!values.length || values.some(isBlankFilterValue)) return undefined;
  if (operator === 'BETWEEN' && values.length !== 2) return undefined;

  if (!dataType || !isNumericDataType(dataType)) return values;

  const numericValues = values.map((item) => {
    if (typeof item === 'number') return Number.isFinite(item) ? item : undefined;
    if (typeof item !== 'string') return undefined;
    const parsedValue = Number(item);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  });
  if (numericValues.some((item) => item === undefined)) return undefined;
  return numericValues as number[];
}

function toFieldFilter(
  tableDefinitionId: string | undefined,
  operatorValue: string | undefined,
  value: unknown,
  dataType?: string
): DisplayTableV2FieldFilter | undefined {
  const operator = mapMuiFilterOperator(operatorValue);
  if (!tableDefinitionId || !operator) return undefined;

  const values = toFilterValues(value, operator, dataType);
  if (!values) return undefined;

  return {
    tableDefinitionId,
    operator,
    values,
  };
}

export function buildDisplayTableV2Request(
  input: DisplayTableV2RequestBuilderInput
): DisplayTableV2FetchRequest {
  const request: DisplayTableV2FetchRequest = {
    subjectId: input.subjectId,
  };

  if (input.procedureId) request.procedureId = input.procedureId;
  if (input.page !== undefined && input.page !== DEFAULT_PAGE) request.page = input.page;
  if (input.size !== undefined && input.size !== DEFAULT_PAGE_SIZE) request.size = input.size;
  if (input.search) request.search = input.search;

  const filters = (input.filters ?? [])
    .map((filter) =>
      toFieldFilter(
        filter.columnField,
        filter.operatorValue ?? filter.operator,
        filter.value,
        input.columnDataTypes?.[filter.columnField]
      )
    )
    .filter((filter): filter is DisplayTableV2FieldFilter => Boolean(filter));
  const fallbackFilter = toFieldFilter(
    input.filterColumn,
    input.filterOperator,
    input.filterValue,
    input.filterColumn ? input.columnDataTypes?.[input.filterColumn] : undefined
  );
  if (filters.length) request.filters = filters;
  else if (fallbackFilter) request.filters = [fallbackFilter];

  const sort = (input.sort ?? [])
    .map((sortItem) => {
      const direction = mapSortDirection(sortItem.sort ?? undefined);
      if (!sortItem.field || !direction) return undefined;
      return {
        tableDefinitionId: sortItem.field,
        direction,
      };
    })
    .filter(
      (sortItem): sortItem is { tableDefinitionId: string; direction: DisplayTableV2SortDirection } =>
        Boolean(sortItem)
    );
  const fallbackSortDirection = mapSortDirection(input.sortType);
  if (sort.length) request.sort = sort;
  else if (input.sortBy && fallbackSortDirection) {
    request.sort = [
      {
        tableDefinitionId: input.sortBy,
        direction: fallbackSortDirection,
      },
    ];
  }

  const hierarchyFilters = Object.entries(input.hierarchyFilters ?? {}).map(([tableDefinitionId, value]) => ({
    tableDefinitionId,
    operator: 'EQUALS' as const,
    values: [value],
  }));
  if (hierarchyFilters.length) request.hierarchyFilters = hierarchyFilters;

  return request;
}

function isNumericDataType(dataType: string): boolean {
  return ['TYPE_NUMBER', 'TYPE_INTEGER', 'TYPE_PERCENTAGE', 'TYPE_CURRENCY'].includes(dataType);
}

function toLegacyColumn(column: DisplayTableV2FetchResponse['columns'][number]): DisplayTableV2LegacyColumn {
  return {
    field: column.tableDefinitionId,
    headerName: column.headerName,
    tableDefinitionId: column.tableDefinitionId,
    colSubjectId: column.relatedSubjectId ?? '',
    type: column.dataType,
    width: 150,
    editable: column.editable,
    sortable: column.capabilities.serverSortable,
    filter: column.capabilities.serverFilterable,
    filterable: column.capabilities.serverFilterable,
    resizable: true,
    hide: false,
    readOnlyColumn: !column.editable,
    pinned: false,
    dataTypeCode: column.dataType,
    renderType: column.renderType,
    allDropdownOptions: [],
    dropdownOptions: [],
    ruleTypeCode: column.ruleTypeCode,
    renderAsRuleTypeCode: '',
    renderAsRuleTypeParams: '',
    ktreeOptions: [],
    ruleTypeParams: '',
    nullable: column.nullable,
    numeric: isNumericDataType(column.dataType),
    allowEmpty: column.allowEmpty,
    displayOrder: column.displayOrder,
    capabilities: column.capabilities,
    optionSource: column.optionSource,
  };
}

export function mapDisplayTableV2ResponseToLegacy(
  response: DisplayTableV2FetchResponse
): DisplayTableV2LegacyResponse {
  return {
    data: response.rows.map(
      (row) =>
        ({
          id: row.id,
          ...row.values,
        } as unknown as DynamicDataItem)
    ),
    columns: response.columns.map(toLegacyColumn),
    columnInitialValues: response.initialValues as unknown as DynamicDataItem,
    layoutView: response.layout,
    rowCount: response.pagination.totalRows,
    totalPage: response.pagination.totalPages,
    currentPage: response.pagination.page,
    totalDependentCube: 0,
    cubeSyncStatus: null,
    // V2 cellFormatting is a different rule contract and must not be exposed
    // as the legacy field-value-to-colour map.
    cellColor: {},
  };
}
